import ButtonGroupActions, { Action } from '@/components/ButtonGroupActions'
import PrivacyField from '@/components/PrivacyField'
import { useModal } from '@/contexts/modal'
import EditIcons from '@/icons/EditIcons'
import { Service } from '@/modules/Service/types'
import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { getEmployees } from '@/modules/Employee/services'
import PersonIcons from '@/icons/PersonIcons'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ServiceAdditionalProps {
    service: Service
    showMoreDetails: boolean
}

const ServiceAdditional: React.FC<ServiceAdditionalProps> = ({
    service,
    showMoreDetails,
}) => {
    const { t } = useTranslation()
    const { setModal } = useModal()

    const { data: employees } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEES],
        queryFn: () => getEmployees({ page: 1, page_size: 1000, q: '' }),
    })

    const serviceActions: Partial<Action & { hidden?: boolean }>[] = [
        {
            label: t('edit-service-type'),
            onClick: () => {
                setModal('ServiceModals/EditServiceTypeModal/index', {
                    serviceID: service?.service_id,
                    service,
                })
            },
            variant: 'outlined',
            icon: <EditIcons />,
        },
        {
            label: t('edit-employee-sale-tracking'),
            onClick: () => {
                setModal('ServiceModals/EditEmployeeSaleTrackingModal/index', {
                    serviceID: service?.service_id,
                    service,
                })
            },
            variant: 'outlined',
            icon: <PersonIcons />,
        },
        {
            label: t('edit-service-currency-exchange-rate'),
            onClick: () => {
                setModal(
                    'ServiceModals/EditServiceCurrencyExchangeRateModal/index',
                    {
                        serviceID: service?.service_id,
                        service,
                    }
                )
            },
            variant: 'outlined',
            icon: <EditIcons />,
        },
    ]

    const saleType =
        service?.service_type === 'upsale'
            ? t('service_type.upsale')
            : t('service_type.new-selling')
    const isLiquidation = service?.service_template === 'Device.Liquidation'

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-2 transition-all duration-200">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <Typogaphy className="font-medium text-gray-900">
                        {t('service-additional-details')}
                    </Typogaphy>
                </div>
            </div>

            {showMoreDetails && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flex flex-col space-y-1 col-span-2">
                            <Typogaphy className="text-sm font-medium text-gray-600">
                                {t('sale-type')}
                            </Typogaphy>
                            <Chip
                                size="small"
                                variant="outlined"
                                color={
                                    service?.service_type === 'upsale'
                                        ? 'primary'
                                        : 'success'
                                }
                                label={saleType}
                                className="w-fit uppercase min-w-[80px] flex items-center justify-center"
                            />
                        </div>
                        {service?.service_creatable_uuid && (
                            <PrivacyField
                                className="col-span-2"
                                value={service.service_creatable_uuid}
                                label={t('service-creatable-uuid')}
                                type="uuid"
                            />
                        )}
                        <div className="flex flex-col space-y-1 col-span-2">
                            <Typogaphy className="text-sm font-medium text-gray-600">
                                {t('service-currency')}
                            </Typogaphy>
                            <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={`${service?.service_currency}`}
                                className="w-fit uppercase min-w-[80px] flex items-center justify-center"
                            />
                        </div>
                        <div className="flex flex-col space-y-1 col-span-2">
                            <Typogaphy className="text-sm font-medium text-gray-600">
                                {t('service-exchange-rate')}
                            </Typogaphy>
                            <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={`${service?.service_exchange_rate}`}
                                className="w-fit uppercase min-w-[80px] flex items-center justify-center"
                            />
                        </div>

                        {isLiquidation && (
                            <div className="flex flex-col space-y-1 col-span-1">
                                <Typogaphy className="text-sm font-medium text-gray-600">
                                    {t('special-service-type')}
                                </Typogaphy>
                                <Chip
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    label={t('liquidation-service')}
                                    className="w-fit min-w-[80px] flex items-center justify-center"
                                />
                            </div>
                        )}

                        <div className="flex flex-col space-y-1 col-span-2">
                            <Typogaphy className="text-sm font-medium text-gray-600">
                                {t('services.commissions')}
                            </Typogaphy>
                            <div className="flex flex-wrap gap-2">
                                {service?.employee_sale_tracking_ids?.length >
                                0 ? (
                                    service.employee_sale_tracking_ids.map(
                                        (empId) => {
                                            const employee =
                                                employees?.results?.find(
                                                    (emp) =>
                                                        emp.employee_id ===
                                                        empId
                                                )
                                            return (
                                                <Chip
                                                    key={empId}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                    label={
                                                        employee
                                                            ? `${employee.employee_name} (${employee.employee_code})`
                                                            : `ID: ${empId}`
                                                    }
                                                    className="text-xs"
                                                />
                                            )
                                        }
                                    )
                                ) : (
                                    <Typogaphy className="text-sm text-gray-500 italic">
                                        {t('no-employees-assigned')}
                                    </Typogaphy>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-2 border-t border-gray-100">
                <ButtonGroupActions
                    actions={
                        serviceActions?.filter(
                            (action) => !action?.hidden
                        ) as Action[]
                    }
                />
            </div>
        </div>
    )
}

export default ServiceAdditional
